import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { fetchMembershipAsOf, previewMembershipUpdate, saveMembershipUpdate } from '../api/membershipApi';
import type { MembershipAsOf, MembershipPreview, MembershipTallies } from '../types';
import { localTodayIso } from '../utils/membershipFormat';
import '../membership.css';

interface Props {
  show: boolean;
  apiPath: string;
  onHide: () => void;
  onSaved: () => void;
  /** Earliest date of change allowed (YYYY-MM-DD). */
  minDate?: string;
  /** Latest date of change allowed (YYYY-MM-DD). Never later than today. */
  maxDate?: string;
  /** Short line under the date field. */
  dateHint?: string;
  /** Base path for the review and save calls when they differ from apiPath. */
  submitPath?: string;
}

function clampDate(value: string, min?: string, max?: string): string {
  if (min && value < min) return min;
  if (max && value > max) return max;
  return value;
}

const emptyTallies: MembershipTallies = {
  added: 0,
  transferred_in: 0,
  transferred_out: 0,
  deceased: 0,
  excluded: 0,
};

function tallyLabel(value: number): string {
  return `In-app record: ${value.toLocaleString()}`;
}

interface MovementRowProps {
  label: string;
  sign: '+' | '-';
  record: number;
  value: number;
  onChange: (value: number) => void;
}

const MovementRow: React.FC<MovementRowProps> = ({ label, sign, record, value, onChange }) => {
  const id = `membership-move-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className="membership-move">
      <div className="membership-move__text">
        <label htmlFor={id} className="membership-move__label">
          <span className={`membership-move__sign ${sign === '+' ? 'is-up' : 'is-down'}`} aria-hidden="true">
            {sign === '+' ? '+' : '\u2212'}
          </span>
          {label}
        </label>
        <div className="membership-move__record">{tallyLabel(record)}</div>
      </div>
      <div className="membership-stepper">
        <button type="button" aria-label={`Decrease ${label}`} onClick={() => onChange(Math.max(0, value - 1))} disabled={value <= 0}>
          {'\u2212'}
        </button>
        <input
          id={id}
          type="number"
          min={0}
          inputMode="numeric"
          value={value}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(Math.max(0, Number(event.target.value) || 0))}
        />
        <button type="button" aria-label={`Increase ${label}`} onClick={() => onChange(value + 1)}>
          +
        </button>
      </div>
    </div>
  );
};

const UpdateMembershipModal: React.FC<Props> = ({
  show,
  apiPath,
  onHide,
  onSaved,
  minDate,
  maxDate,
  dateHint,
  submitPath,
}) => {
  const today = localTodayIso();
  const latest = maxDate && maxDate < today ? maxDate : today;
  const defaultDate = clampDate(today, minDate, latest);
  const savePath = submitPath ?? apiPath;
  const [date, setDate] = useState(defaultDate);
  const [asOf, setAsOf] = useState<MembershipAsOf | null>(null);
  const [added, setAdded] = useState(0);
  const [transferredIn, setTransferredIn] = useState(0);
  const [transferredOut, setTransferredOut] = useState(0);
  const [deceased, setDeceased] = useState(0);
  const [excluded, setExcluded] = useState(0);
  const [male, setMale] = useState(0);
  const [female, setFemale] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [splitError, setSplitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<MembershipPreview | null>(null);

  useEffect(() => {
    if (!show) return;
    setDate((value) => clampDate(value, minDate, latest));
    setAdded(0);
    setTransferredIn(0);
    setTransferredOut(0);
    setDeceased(0);
    setExcluded(0);
    setSplitError(null);
    setError(null);
  }, [show, minDate, latest]);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setError(null);
    fetchMembershipAsOf(apiPath, date).then((response) => {
      if (cancelled) return;
      if (!response.isSuccess() || !response.getData()) {
        setError(response.getError()?.detail ?? 'Please reload and try again.');
        return;
      }
      const data = response.getData()!;
      setAsOf(data);
      setMale(data.male_count);
      setFemale(data.female_count);
    });
    return () => {
      cancelled = true;
    };
  }, [show, apiPath, date]);

  const newTotal = useMemo(() => {
    const base = asOf?.total_members ?? 0;
    return base + added + transferredIn - transferredOut - deceased - excluded;
  }, [asOf, added, transferredIn, transferredOut, deceased, excluded]);

  const tallies = asOf?.tallies ?? emptyTallies;
  const delta = newTotal - (asOf?.total_members ?? 0);
  const deltaText =
    delta === 0 ? 'no change' : delta > 0 ? `\u25b2 +${delta.toLocaleString()}` : `\u25bc \u2212${Math.abs(delta).toLocaleString()}`;
  const deltaClass = delta === 0 ? 'is-flat' : delta > 0 ? 'is-up' : 'is-down';
  const dateLabel = new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const payload = {
    effective_date: date,
    last_membership_entry_id: asOf?.last_membership_entry_id ?? null,
    added_count: added,
    transferred_in_count: transferredIn,
    deceased_count: deceased,
    transferred_out_count: transferredOut,
    excluded_count: excluded,
    male_count: male,
    female_count: female,
  };

  const review = async () => {
    const entered = male + female;
    if (entered !== newTotal) {
      setSplitError(
        `Male and Female must add up to the new total (${newTotal.toLocaleString()}). They currently add up to ${entered.toLocaleString()}.`
      );
      setError(null);
      return;
    }
    setSplitError(null);
    setBusy(true);
    setError(null);
    const response = await previewMembershipUpdate(savePath, payload);
    setBusy(false);
    if (!response.isSuccess() || !response.getData()) {
      setError(response.getError()?.detail ?? 'Please check the figures and try again.');
      return;
    }
    setPreview(response.getData());
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    const response = await saveMembershipUpdate(savePath, payload);
    setBusy(false);
    if (!response.isSuccess()) {
      setError(response.getError()?.detail ?? 'Please reload and try again.');
      return;
    }
    setPreview(null);
    onSaved();
    onHide();
  };

  return (
    <>
      <Modal show={show && !preview} onHide={onHide} centered backdrop="static" dialogClassName="membership-update-modal">
        <Modal.Header closeButton className="membership-update-modal__header">
          <div>
            <Modal.Title>Update membership</Modal.Title>
            <p className="text-muted small mb-0">Record membership changes for this church.</p>
          </div>
        </Modal.Header>
        <Modal.Body className="membership-update-modal__body">
          {error && <Alert variant="warning">{error}</Alert>}
          <div className="membership-update-modal__top">
            <Form.Group controlId="membership-update-date" className="membership-update-modal__date">
              <Form.Label>Date of change</Form.Label>
              <Form.Control
                type="date"
                className="membership-date-input"
                min={minDate}
                max={latest}
                value={date}
                onChange={(event) => setDate(event.target.value)}
                onClick={(event) => {
                  const input = event.currentTarget as HTMLInputElement & { showPicker?: () => void };
                  try {
                    input.showPicker?.();
                  } catch {
                    // Some browsers block the picker here; typing the date still works.
                  }
                }}
              />
              {dateHint && <Form.Text className="membership-hint">{dateHint}</Form.Text>}
            </Form.Group>
            <div className="membership-update-modal__current">
              {date === today ? 'Current total' : `Total on ${dateLabel}`}{' '}
              <strong>{(asOf?.total_members ?? 0).toLocaleString()}</strong>
              <span className="membership-update-modal__sep">{'\u00b7'}</span>
              Male <strong>{(asOf?.male_count ?? 0).toLocaleString()}</strong>
              <span className="membership-update-modal__sep">{'\u00b7'}</span>
              Female <strong>{(asOf?.female_count ?? 0).toLocaleString()}</strong>
            </div>
          </div>

          <h3 className="membership-update-modal__section">Membership Increase</h3>
          <MovementRow label="Added" sign="+" record={tallies.added} value={added} onChange={setAdded} />
          <MovementRow label="Transferred in" sign="+" record={tallies.transferred_in} value={transferredIn} onChange={setTransferredIn} />

          <h3 className="membership-update-modal__section">Membership Decrease</h3>
          <MovementRow label="Deceased" sign="-" record={tallies.deceased} value={deceased} onChange={setDeceased} />
          <MovementRow label="Transferred out" sign="-" record={tallies.transferred_out} value={transferredOut} onChange={setTransferredOut} />
          <MovementRow label="Excluded" sign="-" record={tallies.excluded} value={excluded} onChange={setExcluded} />

          <h3 className="membership-update-modal__section">Male and Female</h3>
          <Row className="g-3">
            <Col xs={6}>
              <Form.Group controlId="membership-update-male">
                <Form.Label>Male</Form.Label>
                <Form.Control type="number" min={0} value={male} onChange={(event) => setMale(Number(event.target.value))} />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group controlId="membership-update-female">
                <Form.Label>Female</Form.Label>
                <Form.Control type="number" min={0} value={female} onChange={(event) => setFemale(Number(event.target.value))} />
              </Form.Group>
            </Col>
          </Row>
          {splitError && <p className="text-danger small mt-2 mb-0">{splitError}</p>}
        </Modal.Body>
        <Modal.Footer className="membership-update-modal__footer justify-content-between">
          <div>
            <div className="membership-update-modal__total-label">New total</div>
            <div className="membership-update-modal__total">
              {newTotal.toLocaleString()} <span className={`membership-update-modal__delta ${deltaClass}`}>{deltaText}</span>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={onHide}>Cancel</Button>
            <Button variant="primary" onClick={review} disabled={busy}>
              {busy ? <Spinner size="sm" animation="border" /> : 'Review update'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(preview)} onHide={() => setPreview(null)} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Confirm membership update</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="warning">{error}</Alert>}
          <p>Please check these updates before they are saved.</p>
          {preview && (
            <>
              <dl className="row mb-0">
                <dt className="col-6">Date of change</dt>
                <dd className="col-6">{preview.effective_date_label}</dd>
                <dt className="col-6">Total on that date</dt>
                <dd className="col-6">{preview.as_of_total.toLocaleString()}</dd>
                <dt className="col-6">Added (+)</dt>
                <dd className="col-6">+ {preview.added_count.toLocaleString()}</dd>
                <dt className="col-6">Transferred in (+)</dt>
                <dd className="col-6">+ {preview.transferred_in_count.toLocaleString()}</dd>
                <dt className="col-6">Deceased (−)</dt>
                <dd className="col-6">− {preview.deceased_count.toLocaleString()}</dd>
                <dt className="col-6">Transferred out (−)</dt>
                <dd className="col-6">− {preview.transferred_out_count.toLocaleString()}</dd>
                <dt className="col-6">Excluded (−)</dt>
                <dd className="col-6">− {preview.excluded_count.toLocaleString()}</dd>
              </dl>
              <hr className="my-2" />
              <dl className="row mb-0">
                <dt className="col-6 fw-bold">New total</dt>
                <dd className="col-6 fw-bold">{preview.new_total.toLocaleString()}</dd>
                <dt className="col-6">Male</dt>
                <dd className="col-6">{preview.as_of_male === preview.male_count ? `${preview.male_count.toLocaleString()} (no change)` : `${preview.as_of_male.toLocaleString()} → ${preview.male_count.toLocaleString()}`}</dd>
                <dt className="col-6">Female</dt>
                <dd className="col-6">{preview.as_of_female === preview.female_count ? `${preview.female_count.toLocaleString()} (no change)` : `${preview.as_of_female.toLocaleString()} → ${preview.female_count.toLocaleString()}`}</dd>
              </dl>
            </>
          )}
          {preview?.later_message && <p className="mt-3 mb-0">{preview.later_message}</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPreview(null)}>Back</Button>
          <Button variant="primary" onClick={confirm} disabled={busy}>Confirm and save</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UpdateMembershipModal;
